import React, { useState, useRef, useEffect } from 'react';

const AIAdvisor = ({ isAdmin }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Load insights when panel opens
  useEffect(() => {
    if (isOpen && !insights) {
      loadInsights();
    }
  }, [isOpen]);
  
  // Don't render if not admin
  if (!isAdmin) return null;
  
  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const token = localStorage.getItem('lotsense_access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://lotsense-production.up.railway.app/api'}/ai/insights`, {
        headers: {
          'Authorization': `Bearer ${token}` 
        }
      });
      
      if (!response.ok) throw new Error('Failed to load insights');
      
      const data = await response.json();
      setInsights(data.insights);
      setStats(data.stats);
    } catch (error) {
      console.error('Failed to load insights:', error);
      setInsights('Failed to load insights. Try refreshing.');
    } finally {
      setInsightsLoading(false);
    }
  };
  
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('lotsense_access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://lotsense-production.up.railway.app/api'}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          message: userMessage,
          history: messages.slice(-10)
        })
      });
      
      if (!response.ok) throw new Error('AI request failed');
      
      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      if (data.stats) setStats(data.stats);
    } catch (error) {
      console.error('AI Chat Error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Something went wrong. Try again.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickQuestions = [
    "What needs my attention today?",
    "Which cars should I dump?",
    "What's killing my margin?",
    "Recon status check"
  ];
  
  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed top-20 right-0 z-50 px-3 py-6 text-white text-sm font-medium shadow-lg transition-all ${
          isOpen ? 'bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'
        }`}
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
      >
        {isOpen ? '✕ Close' : '🤖 AI Advisor'}
      </button>
      
      {/* Slide-out Panel */}
      <div className={`fixed top-0 right-0 h-full bg-gray-900 text-white shadow-2xl z-40 transition-transform duration-300 flex flex-col ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`} style={{ width: '420px' }}>
        
        {/* Header */}
        <div className="bg-gray-800 px-4 py-3 border-b border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-bold text-lg">AI Business Advisor</h2>
              <p className="text-xs text-gray-400">Sees everything. Tells it straight.</p>
            </div>
            <button 
              onClick={loadInsights}
              className="text-xs bg-blue-600 hover:bg-blue-500 px-2 py-1 rounded"
              disabled={insightsLoading}
            >
              {insightsLoading ? '...' : '↻ Refresh'}
            </button>
          </div>
          
          {/* Quick Stats */}
          {stats && (
            <div className="flex gap-3 mt-3 text-xs">
              <div className="bg-gray-700 px-2 py-1 rounded">
                <span className="text-gray-400">Units:</span> {stats.total_active}
              </div>
              <div className="bg-gray-700 px-2 py-1 rounded">
                <span className="text-gray-400">Avg Days:</span> {Math.round(stats.avg_days_on_lot || 0)}
              </div>
              <div className={`px-2 py-1 rounded ${stats.over_30_days > 0 ? 'bg-red-900' : 'bg-gray-700'}`}>
                <span className="text-gray-400">30+ Days:</span> {stats.over_30_days || 0}
              </div>
            </div>
          )}
        </div>
        
        {/* Insights Section */}
        <div className="bg-gray-800 px-4 py-3 border-b border-gray-700 max-h-64 overflow-y-auto">
          <h3 className="text-xs font-semibold text-gray-400 uppercase mb-2">Right Now</h3>
          {insightsLoading ? (
            <div className="text-gray-400 text-sm animate-pulse">Analyzing your business...</div>
          ) : insights ? (
            <div className="text-sm text-gray-200 whitespace-pre-wrap">{insights}</div>
          ) : (
            <div className="text-gray-500 text-sm">Click refresh to load insights</div>
          )}
        </div>
        
        {/* Quick Questions */}
        <div className="px-4 py-2 border-b border-gray-700 flex gap-2 flex-wrap">
          {quickQuestions.map((q, i) => (
            <button
              key={i}
              onClick={() => setInput(q)}
              className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-gray-300"
            >
              {q}
            </button>
          ))}
        </div>
        
        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-gray-500 text-sm text-center mt-4">
              Ask me anything about the business.
              <br />I can see all your inventory, recon, titles, everything.
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                msg.role === 'user' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-100'
              }`}>
                {msg.content}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-700 text-gray-300 px-3 py-2 rounded-lg text-sm">
                <span className="animate-pulse">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <form onSubmit={sendMessage} className="border-t border-gray-700 p-3 flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about inventory, recon, what to dump..."
            className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white px-4 py-2 rounded-lg text-sm transition-colors"
          >
            Send
          </button>
        </form>
        
        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-700 text-xs text-gray-500 text-center">
          Loads full business context on every message. ~$0.02-0.05 per chat.
        </div>
      </div>
    </>
  );
};

export default AIAdvisor;
